import React, { useRef, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Check, ChevronDown, Loader2, Plus, Trash2, Upload, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  ProductResponse,
  AngleEnum,
  ANGLE_OPTIONS,
  ANGLE_LABEL,
} from "@/types/product-sku.types";
import { useCreateProductSKU } from "@/hooks/useProductSKU";
import { uploadFileAndReturnUrl } from "@/services/api/gcs.service";
import { useBrandUpdatesStore } from "@/store/brand-updates.store";

// ─── Schema ───────────────────────────────────────────────────────────────────

const angleEnum = z.enum([
  "front", "side", "back", "3/4", "top", "sole", "detail", "other",
]);

const variantFormSchema = z.object({
  id: z.string(),
  name: z.string().min(1, "Variant name is required"),
  products: z
    .array(
      z.object({
        gallery_item_id: z.string(),
        product_url: z.string(),
        angle: angleEnum,
      })
    )
    .min(1, "At least one image is required per variant"),
});

const createProductSchema = z.object({
  name: z.string().min(1, "Product name is required"),
  sku_code: z.string().optional(),
  category: z.string().optional(),
  description: z.string().optional(),
  variants: z.array(variantFormSchema).min(1),
});

type CreateProductFormValues = z.infer<typeof createProductSchema>;

// ─── Category combobox ────────────────────────────────────────────────────────

function CategoryCombobox({
  value,
  onChange,
  categories,
}: {
  value: string;
  onChange: (val: string) => void;
  categories: string[];
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const trimmed = search.trim();
  const isNew =
    trimmed.length > 0 &&
    !categories.some((c) => c.toLowerCase() === trimmed.toLowerCase());
  const filtered = categories.filter((c) =>
    c.toLowerCase().includes(trimmed.toLowerCase())
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between rounded-xl border-gray-200 font-normal",
            !value && "text-muted-foreground"
          )}
        >
          <span className="truncate">{value || "Select category..."}</span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search or add category..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            {filtered.length === 0 && !isNew && (
              <CommandEmpty>No categories found.</CommandEmpty>
            )}
            {filtered.length > 0 && (
              <CommandGroup>
                {filtered.map((cat) => (
                  <CommandItem
                    key={cat}
                    value={cat}
                    onSelect={() => {
                      onChange(cat === value ? "" : cat);
                      setSearch("");
                      setOpen(false);
                    }}
                    className="cursor-pointer"
                  >
                    <Check
                      className={cn("mr-2 h-4 w-4", value === cat ? "opacity-100" : "opacity-0")}
                    />
                    {cat}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {isNew && (
              <CommandGroup heading="Add new">
                <CommandItem
                  value={`__add__${trimmed}`}
                  onSelect={() => {
                    onChange(trimmed);
                    setSearch("");
                    setOpen(false);
                  }}
                  className="cursor-pointer text-primary gap-2"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add &ldquo;{trimmed}&rdquo;
                </CommandItem>
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// ─── Image card with angle dropdown ──────────────────────────────────────────

function ProductImageCard({
  product,
  isSelected,
  onSelect,
  onAngleChange,
  onRemove,
}: {
  product: ProductResponse;
  isSelected: boolean;
  onSelect: (key: string) => void;
  onAngleChange: (key: string, angle: AngleEnum) => void;
  onRemove: (key: string) => void;
}) {
  const key = product.gallery_item_id;

  return (
    <div
      onClick={() => onSelect(key)}
      className={cn(
        "relative rounded-xl overflow-hidden border bg-white cursor-pointer transition-all",
        isSelected ? "border-primary ring-2 ring-primary/30" : "border-gray-200"
      )}
    >
      <div className="aspect-square bg-gray-50">
        <img
          src={product.product_url}
          alt={product.angle}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      </div>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onRemove(key);
        }}
        className="absolute top-1.5 right-1.5 w-5 h-5 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center shadow transition-colors"
        aria-label="Remove image"
      >
        <X className="h-2.5 w-2.5 text-white" />
      </button>
      <div className="border-t border-gray-100 bg-white" onClick={(e) => e.stopPropagation()}>
        <Select
          value={product.angle}
          onValueChange={(val) => onAngleChange(key, val as AngleEnum)}
        >
          <SelectTrigger className="h-8 w-full rounded-none border-0 px-2.5 text-xs font-medium text-gray-700 focus:ring-0 focus:ring-offset-0 shadow-none">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ANGLE_OPTIONS.map((a) => (
              <SelectItem key={a} value={a} className="text-xs">
                {ANGLE_LABEL[a]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

// ─── CreateProductModal ───────────────────────────────────────────────────────

interface CreateProductModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  brandId: string;
}

export function CreateProductModal({
  open,
  onOpenChange,
  brandId,
}: CreateProductModalProps) {
  const [selectedImageKey, setSelectedImageKey] = useState<string | null>(null);
  const [uploadingIdx, setUploadingIdx] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const activeVariantIdxRef = useRef<number>(0);
  const { mutate: createSKU, isPending } = useCreateProductSKU(brandId);
  const allCategories =
    useBrandUpdatesStore((s) => s.data?.brand_information?.static?.categories) ?? [];

  const form = useForm<CreateProductFormValues>({
    resolver: zodResolver(createProductSchema),
    defaultValues: {
      name: "",
      sku_code: "",
      category: "",
      description: "",
      variants: [{ id: crypto.randomUUID(), name: "", products: [] }],
    },
  });

  const { fields: variantFields, append: appendVariant, remove: removeVariant } =
    useFieldArray({ control: form.control, name: "variants" });

  const variants = form.watch("variants");

  const handleAngleChange = (variantIdx: number, key: string, angle: AngleEnum) => {
    const updated = variants[variantIdx].products.map((p) =>
      p.gallery_item_id === key ? { ...p, angle } : p
    );
    form.setValue(`variants.${variantIdx}.products`, updated, { shouldValidate: true });
  };

  const handleRemoveImage = (variantIdx: number, key: string) => {
    const updated = variants[variantIdx].products.filter((p) => p.gallery_item_id !== key);
    form.setValue(`variants.${variantIdx}.products`, updated, { shouldValidate: true });
    if (selectedImageKey === key) setSelectedImageKey(null);
  };

  const handleAddImage = (variantIdx: number) => {
    activeVariantIdxRef.current = variantIdx;
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const allFiles = Array.from(e.target.files ?? []);
    e.target.value = "";
    if (allFiles.length === 0) return;

    const variantIdx = activeVariantIdxRef.current;
    const currentProducts = form.getValues(`variants.${variantIdx}.products`);
    const slotsAvailable = 10 - currentProducts.length;
    if (slotsAvailable <= 0) return;

    const files = allFiles.slice(0, slotsAvailable);
    if (allFiles.length > slotsAvailable) {
      toast.warning(
        `Only ${slotsAvailable} slot${slotsAvailable !== 1 ? "s" : ""} remaining. Uploading first ${slotsAvailable}.`
      );
    }

    setUploadingIdx(variantIdx);
    try {
      const urls = await Promise.all(
        files.map((file) =>
          uploadFileAndReturnUrl(file.name, file.type, "product-sku", file, brandId)
        )
      );
      const newProducts: ProductResponse[] = urls.map((url) => ({
        gallery_item_id: crypto.randomUUID(),
        product_url: url,
        angle: "front" as AngleEnum,
      }));
      form.setValue(
        `variants.${variantIdx}.products`,
        [...currentProducts, ...newProducts],
        { shouldValidate: true }
      );
      setSelectedImageKey(newProducts[newProducts.length - 1].gallery_item_id);
      toast.success(`${urls.length} image${urls.length !== 1 ? "s" : ""} uploaded`);
    } catch {
      toast.error("Upload failed. Please try again.");
    } finally {
      setUploadingIdx(null);
    }
  };

  const handleClose = (o: boolean) => {
    if (!o) {
      form.reset();
      setSelectedImageKey(null);
    }
    onOpenChange(o);
  };

  const onSubmit = (values: CreateProductFormValues) => {
    const variants = values.variants.map((v) => ({
      name: v.name,
      products: v.products.map(({ product_url, angle }) => ({ url: product_url, angle })),
    }));
    const payload = {
      name: values.name.trim(),
      sku_code: values.sku_code?.trim() || null,
      category: values.category?.trim() || null,
      description: values.description?.trim() || null,
      variants,
    };
    console.log("[CreateProductModal] payload:", payload);
    handleClose(false);
    const toastId = toast.loading("Analysing product...");
    createSKU(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      payload as any,
      {
        onSuccess: () => toast.success("Product created", { id: toastId }),
        onError: (err) => toast.error(`Failed to create: ${err.message}`, { id: toastId }),
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="min-w-[720px] max-h-[90vh] flex flex-col p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-6 pt-5 pb-4 border-b border-gray-100 shrink-0">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Plus className="h-4 w-4 text-muted-foreground" />
            Create Product
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col flex-1 overflow-hidden"
          >
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
              {/* Product Name */}
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium">Product Name</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="Enter product name"
                        className="rounded-xl border-gray-200"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* SKU Code + Category row */}
              <div className="grid grid-cols-2 gap-3">
                <FormField
                  control={form.control}
                  name="sku_code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium">SKU Code</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="e.g. BIRK-ARIZONA"
                          className="rounded-xl border-gray-200"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="category"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium">Category</FormLabel>
                      <FormControl>
                        <CategoryCombobox
                          value={field.value ?? ""}
                          onChange={field.onChange}
                          categories={allCategories}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Description */}
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium">Product Description</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        placeholder="Describe the product..."
                        rows={3}
                        className="resize-none rounded-xl border-gray-200"
                      />
                    </FormControl>
                    <p className="text-[11px] text-muted-foreground">
                      Used in image prompts. Edit to correct any inaccuracies.
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Variants */}
              <div className="space-y-3">
                <span className="text-sm font-medium">
                  Variants ({variantFields.length})
                </span>

                {variantFields.map((field, variantIdx) => {
                  const variantProducts = variants[variantIdx]?.products ?? [];
                  const isUploadingThis = uploadingIdx === variantIdx;

                  return (
                    <div
                      key={field.id}
                      className="rounded-xl border border-gray-200 p-4 space-y-3 bg-gray-50/50"
                    >
                      {/* Variant name row */}
                      <div className="flex items-start gap-2">
                        <FormField
                          control={form.control}
                          name={`variants.${variantIdx}.name`}
                          render={({ field: nameField }) => (
                            <FormItem className="flex-1">
                              <FormControl>
                                <Input
                                  {...nameField}
                                  placeholder={`Variant ${variantIdx + 1} name (e.g. Black, Size M)`}
                                  className="rounded-xl border-gray-200 bg-white"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        {variantFields.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeVariant(variantIdx)}
                            className="shrink-0 mt-0.5 p-2 rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                            aria-label="Remove variant"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>

                      {/* Images grid */}
                      <div className="grid grid-cols-4 gap-2">
                        {variantProducts.map((p) => (
                          <ProductImageCard
                            key={p.gallery_item_id}
                            product={p}
                            isSelected={selectedImageKey === p.gallery_item_id}
                            onSelect={setSelectedImageKey}
                            onAngleChange={(key, angle) =>
                              handleAngleChange(variantIdx, key, angle)
                            }
                            onRemove={(key) => handleRemoveImage(variantIdx, key)}
                          />
                        ))}

                        {variantProducts.length < 10 && (
                          <button
                            type="button"
                            onClick={() => handleAddImage(variantIdx)}
                            disabled={isUploadingThis}
                            className="aspect-square rounded-xl border-2 border-dashed border-gray-300 hover:border-gray-400 hover:bg-gray-50 flex flex-col items-center justify-center gap-1.5 transition-colors text-muted-foreground hover:text-foreground disabled:opacity-60 disabled:cursor-not-allowed"
                          >
                            {isUploadingThis ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Upload className="h-4 w-4" />
                            )}
                            <span className="text-[10px] font-medium">
                              {isUploadingThis ? "Uploading" : "Add Image"}
                            </span>
                          </button>
                        )}
                      </div>

                      {form.formState.errors.variants?.[variantIdx]?.products && (
                        <p className="text-xs text-destructive">
                          {form.formState.errors.variants[variantIdx]?.products?.message}
                        </p>
                      )}
                    </div>
                  );
                })}

                {/* Add Variant button */}
                <button
                  type="button"
                  onClick={() =>
                    appendVariant({ id: crypto.randomUUID(), name: "", products: [] })
                  }
                  className="w-full rounded-xl border-2 border-dashed border-gray-300 hover:border-primary hover:bg-primary/5 py-3 flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  Add Variant
                </button>
              </div>
            </div>

            <DialogFooter className="px-6 py-4 border-t border-gray-100 shrink-0 flex-row items-center justify-end sm:justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleClose(false)}
                disabled={isPending}
              >
                Cancel
              </Button>
              <Button type="submit" size="sm" disabled={isPending}>
                {isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                Create Product
              </Button>
            </DialogFooter>
          </form>
        </Form>

        {/* Single shared file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={handleFileChange}
        />
      </DialogContent>
    </Dialog>
  );
}
